import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, Sparkles, Wand2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

const fmt = (p) => (p?.length ? p.join(" / ") : "Racine");

function PlanTable({ items, selected, toggle }) {
  return (
    <div className="border rounded-lg max-h-96 overflow-y-auto divide-y" data-testid="reorg-items">
      {items.map((it) => (
        <label key={it.file_id} className={`flex items-start gap-3 px-3 py-2 text-sm cursor-pointer ${it.applied ? "opacity-50" : ""}`}
          data-testid={`reorg-item-${it.file_id}`}>
          <Checkbox checked={selected.has(it.file_id)} disabled={it.applied} onCheckedChange={() => toggle(it.file_id)}
            className="mt-0.5" data-testid={`reorg-check-${it.file_id}`} />
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{it.name}{it.applied && " · déplacé"}</div>
            <div className="text-xs text-slate-500 flex flex-wrap items-center gap-1.5">
              <span>{fmt(it.from)}</span><ArrowRight className="h-3 w-3" /><span className="text-[#1E3A8A] font-medium">{fmt(it.to)}</span>
            </div>
          </div>
        </label>
      ))}
    </div>
  );
}

export function AiTreeCard() {
  const { company } = useCompany();
  const [enabled, setEnabled] = useState(true);
  const [plan, setPlan] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (keep) => {
    const { data } = await api.get("/admin/reorganize/plan");
    setPlan(data);
    if (data.status !== "ready") return;
    const pend = data.items.filter((i) => !i.applied).map((i) => i.file_id);
    setSelected((prev) => new Set(keep ? pend.filter((id) => prev.has(id)) : pend));
  }, []);
  useEffect(() => {
    api.get("/admin/ai-tree").then((r) => setEnabled(r.data.enabled)).catch(() => {});
    load().catch(() => {});
  }, [load]);
  useEffect(() => {
    if (plan?.status !== "running") return undefined;
    const t = setInterval(() => load().catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [plan?.status, load]);

  const pending = useMemo(() => (plan?.items || []).filter((i) => !i.applied), [plan]);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAi = async (v) => {
    try { await api.put("/admin/ai-tree", { enabled: v }); setEnabled(v); toast.success(v ? "L'IA crée l'arborescence au besoin" : "Rangement uniquement selon vos règles"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const start = async () => {
    try { await api.post("/admin/reorganize/plan"); toast.success("Analyse de l'arborescence lancée"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/reorganize/plan/${plan.id}/apply`, { file_ids: [...selected], clean_empty: true });
      toast.success(`${data.moved} document(s) rangé(s)` + (data.folders_trashed ? ` · ${data.folders_trashed} dossier(s) vide(s) mis à la corbeille` : ""));
      load(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="ai-tree-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#1E3A8A]" />Arborescence créée par l'IA</div>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={enabled} onCheckedChange={toggleAi} data-testid="ai-tree-switch" />
          {enabled ? "L'IA invente les dossiers au besoin" : "Uniquement mes règles"}
        </label>
      </div>
      <p className="text-sm text-slate-600">Réorganisez tous les documents de la société « {company?.name} », même ceux déjà rangés : l'IA propose un nouvel emplacement pour chacun, vous cochez ce que vous acceptez, puis seuls les documents cochés sont déplacés. Les dossiers devenus vides vont à la corbeille (restaurables 30 jours). Attention : un document déplacé prend les droits d'accès de son nouveau dossier.</p>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={start} disabled={plan?.status === "running"} data-testid="reorg-plan-btn">
          {plan?.status === "running" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
          {plan?.status === "running" ? `Analyse… ${plan.progress}/${plan.total || "?"}` : "Proposer une réorganisation complète"}
        </Button>
        {plan?.status === "interrupted" && <span className="text-sm text-amber-700" data-testid="reorg-interrupted">Analyse interrompue, relancez-la.</span>}
      </div>
      {plan?.status === "ready" && (
        <div className="space-y-3" data-testid="reorg-plan">
          {pending.length === 0 ? (
            <p className="text-sm text-emerald-700" data-testid="reorg-empty">Tout est déjà bien rangé.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span data-testid="reorg-count">{pending.length} déplacement(s) proposé(s), {selected.size} sélectionné(s)</span>
                <button type="button" className="text-[#1E3A8A] hover:underline" onClick={() => setSelected(new Set(pending.map((i) => i.file_id)))} data-testid="reorg-select-all">Tout cocher</button>
                <button type="button" className="text-[#1E3A8A] hover:underline" onClick={() => setSelected(new Set())} data-testid="reorg-select-none">Tout décocher</button>
              </div>
              <PlanTable items={plan.items} selected={selected} toggle={toggle} />
              <Button onClick={apply} disabled={busy || !selected.size} data-testid="reorg-apply-btn">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{selected.size > 1 ? `Ranger les ${selected.size} documents cochés` : "Ranger le document coché"}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
